import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Button,
  Stack,
  Collapse,
  Icon,
  Link,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useColorModeValue,
  useBreakpointValue,
  useDisclosure,
  Image,
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon, ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useAuth } from '../../context/AuthContext';
import config from '../../config/config';

/**
 * Navbar component for the public-facing pages
 */
const Navbar = () => {
  const { isOpen, onToggle } = useDisclosure();
  const { currentUser, logout, isAdmin, isCommittee } = useAuth();
  const navigate = useNavigate();

  // Handle user logout
  const handleLogout = async () => {
    // Pass navigation as a callback to ensure it happens after state is cleared
    await logout(() => {
      navigate('/');
    });
  };

  // Handle navigation to dashboard based on user role
  const navigateToDashboard = () => {
    if (isAdmin) {
      navigate('/admin/dashboard');
    } else if (isCommittee) {
      navigate('/committee/dashboard');
    }
  };

  return (
    <Box>
      <Flex
        bg={useColorModeValue('white', 'gray.800')}
        color={useColorModeValue('gray.600', 'white')}
        minH={'70px'}
        py={{ base: 3 }}
        px={{ base: 6 }}
        borderBottom={1}
        borderStyle={'solid'}
        borderColor={useColorModeValue('brown.100', 'gray.900')}
        align={'center'}
        boxShadow="sm"
      >
        <Flex
          flex={{ base: 1, md: 'auto' }}
          ml={{ base: -2 }}
          display={{ base: 'flex', md: 'none' }}
        >
          <Button
            onClick={onToggle}
            variant={'ghost'}
            aria-label={'Toggle Navigation'}
          >
            <Icon as={isOpen ? CloseIcon : HamburgerIcon} w={5} h={5} />
          </Button>
        </Flex>
        <Flex flex={{ base: 1 }} justify={{ base: 'center', md: 'start' }}>
          <Link as={RouterLink} to="/">
            <Flex align="center">
              <Image
                src="/images/logo-icon.svg"
                alt="مجالسنا"
                boxSize="32px"
                me={{ base: 2, md: 4 }}
              />
              <Text
                textAlign={useBreakpointValue({ base: 'center', md: 'left' })}
                fontFamily={'heading'}
                color={useColorModeValue('brown.700', 'white')}
                fontWeight="bold"
                fontSize="xl"
              >
                {config.app.name}
              </Text>
            </Flex>
          </Link>

          <Flex display={{ base: 'none', md: 'flex' }} me={10} ms={10} align="center">
            <DesktopNav />
          </Flex>
        </Flex>

        <Stack
          flex={{ base: 1, md: 0 }}
          justify={'flex-end'}
          direction={'row'}
          spacing={6}
        >
          {currentUser ? (
            <>
              <Button
                as={RouterLink}
                fontSize={'sm'}
                fontWeight={500}
                variant={'outline'}
                colorScheme="teal"
                to="/apply"
              >
                تقديم طلب
              </Button>
              <Button
                onClick={navigateToDashboard}
                fontSize={'sm'}
                fontWeight={600}
                colorScheme="brown"
              >
                لوحة التحكم
              </Button>
              <Button
                onClick={handleLogout}
                fontSize={'sm'}
                fontWeight={400}
                variant={'ghost'}
                colorScheme="gray"
              >
                تسجيل الخروج
              </Button>
            </>
          ) : (
            <>
              <Button
                as={RouterLink}
                fontSize={'sm'}
                fontWeight={500}
                variant={'outline'}
                colorScheme="teal"
                to="/apply"
              >
                تقديم طلب
              </Button>
              <Button
                as={RouterLink}
                to="/login"
                fontSize={'sm'}
                fontWeight={600}
                colorScheme="brown"
                display={{ base: 'none', md: 'inline-flex' }}
              >
                تسجيل الدخول
              </Button>
            </>
          )}
        </Stack>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <MobileNav />
      </Collapse>
    </Box>
  );
};

const DesktopNav = () => {
  const linkColor = useColorModeValue('gray.600', 'gray.200');
  const linkHoverColor = useColorModeValue('gray.800', 'white');
  const popoverContentBgColor = useColorModeValue('white', 'gray.800');

  return (
    <Stack direction={'row'} spacing={4}>
      {NAV_ITEMS.map((navItem) => (
        <Box key={navItem.label}>
          <Popover trigger={'hover'} placement={'bottom-start'}>
            <PopoverTrigger>
              <Link
                p={2}
                as={RouterLink}
                to={navItem.href ?? '#'}
                fontSize={'sm'}
                fontWeight={500}
                color={linkColor}
                _hover={{
                  textDecoration: 'none',
                  color: linkHoverColor,
                }}
              >
                {navItem.label}
                {navItem.children && (
                  <Icon
                    as={ChevronDownIcon}
                    w={5}
                    h={5}
                    ml={1}
                    transition={'all .25s ease-in-out'}
                  />
                )}
              </Link>
            </PopoverTrigger>

            {navItem.children && (
              <PopoverContent
                border={0}
                boxShadow={'xl'}
                bg={popoverContentBgColor}
                p={4}
                rounded={'xl'}
                minW={'sm'}
              >
                <Stack>
                  {navItem.children.map((child) => (
                    <DesktopSubNav key={child.label} {...child} />
                  ))}
                </Stack>
              </PopoverContent>
            )}
          </Popover>
        </Box>
      ))}
    </Stack>
  );
};

const DesktopSubNav = ({ label, href, subLabel }) => {
  return (
    <Link
      as={RouterLink}
      to={href}
      role={'group'}
      display={'block'}
      p={2}
      rounded={'md'}
      _hover={{ bg: useColorModeValue('primary.50', 'gray.900') }}
    >
      <Stack direction={'row'} align={'center'}>
        <Box>
          <Text
            transition={'all .3s ease'}
            _groupHover={{ color: 'primary.500' }}
            fontWeight={500}
          >
            {label} 
          </Text>
          <Text fontSize={'sm'}>{subLabel}</Text>
        </Box>
        <Flex
          transition={'all .3s ease'}
          transform={'translateX(-10px)'}
          opacity={0}
          _groupHover={{ opacity: '100%', transform: 'translateX(0)' }}
          justify={'flex-end'}
          align={'center'}
          flex={1}
        >
          <Icon color={'primary.500'} w={5} h={5} as={ChevronRightIcon} />
        </Flex>
      </Stack>
    </Link>
  );
};

const MobileNav = () => {
  return (
    <Stack
      bg={useColorModeValue('white', 'gray.800')}
      p={4}
      display={{ md: 'none' }}
    >
      {NAV_ITEMS.map((navItem) => (
        <MobileNavItem key={navItem.label} {...navItem} />
      ))}
    </Stack>
  );
};

const MobileNavItem = ({ label, children, href }) => {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <Stack spacing={4} onClick={children && onToggle}>
      <Flex
        py={2}
        as={RouterLink}
        to={href ?? '#'}
        justify={'space-between'}
        align={'center'}
        _hover={{
          textDecoration: 'none',
        }}
      >
        <Text
          fontWeight={600}
          color={useColorModeValue('gray.600', 'gray.200')}
        >
          {label}
        </Text>
        {children && (
          <Icon
            as={ChevronDownIcon}
            transition={'all .25s ease-in-out'}
            transform={isOpen ? 'rotate(180deg)' : ''}
            w={6}
            h={6}
          />
        )}
      </Flex>

      <Collapse in={isOpen} animateOpacity style={{ marginTop: '0!important' }}>
        <Stack
          mt={2}
          pl={4}
          borderLeft={1}
          borderStyle={'solid'}
          borderColor={useColorModeValue('gray.200', 'gray.700')}
          align={'start'}
        >
          {children &&
            children.map((child) => (
              <Link
                key={child.label}
                py={2}
                as={RouterLink}
                to={child.href}
              >
                {child.label}
              </Link>
            ))}
        </Stack>
      </Collapse>
    </Stack>
  );
};

// Navigation items
const NAV_ITEMS = [
  {
    label: 'الرئيسية',
    href: '/',
  },
  {
    label: 'لائحة تشكيل مجالس مراكز الأحياء',
    href: '/neighborhood-council-regulations',
  },
  {
    label: 'الخط الزمني',
    href: '/timeline',
  },
  {
    label: 'تتبع حالة الطلب',
    href: '/status',
  },
  {
    label: 'عن هذا المشروع',
    href: '/project-closed',
  },
];

export default Navbar;
